import { z } from "zod";
import {
  AMENITY_CATEGORIES,
  CANCELLATION_POLICIES,
  LOCALES,
  PROPERTY_TYPES,
  ROOM_TYPES,
  PAYMENT_METHODS,
} from "./enums.js";

/** ISO calendar date, no time component: "2026-12-24". Dates are strings end to end — never Date. */
export const isoDate = z.iso.date();

/** Money is always integer minor units (centimes of MAD). */
export const minor = z.number().int().nonnegative();

/** Localised text. French is required (house default); ar/en optional. */
export const localizedText = z.object({
  fr: z.string().min(1),
  ar: z.string().optional(),
  en: z.string().optional(),
});
export type LocalizedText = z.infer<typeof localizedText>;

export const dateRange = z
  .object({ checkIn: isoDate, checkOut: isoDate })
  .refine((v) => v.checkOut > v.checkIn, { message: "checkOut must be after checkIn", path: ["checkOut"] });

// ---------- catalog ----------

export const listingDraftInput = z.object({
  cityId: z.number().int().positive(),
  title: localizedText,
  description: localizedText,
  propertyType: z.enum(PROPERTY_TYPES),
  roomType: z.enum(ROOM_TYPES),
  address: z.string().min(3),
  // Optional for now (a map comes later); null clears a value on update.
  lat: z.number().min(-90).max(90).nullish(),
  lng: z.number().min(-180).max(180).nullish(),
  maxGuests: z.number().int().min(1).max(30),
  bedrooms: z.number().int().min(0).max(30),
  beds: z.number().int().min(1).max(60),
  bathrooms: z.number().min(0.5).max(30),
  basePriceMinor: minor.min(1),
  cleaningFeeMinor: minor.default(0),
  checkInTime: z.string().regex(/^\d{2}:\d{2}$/).default("15:00"),
  checkOutTime: z.string().regex(/^\d{2}:\d{2}$/).default("11:00"),
  minNights: z.number().int().min(1).default(1),
  maxNights: z.number().int().min(1).default(90),
  cancellationPolicy: z.enum(CANCELLATION_POLICIES).default("moderate"),
  houseRules: z.array(z.string().min(1)).default([]),
  amenityIds: z.array(z.number().int().positive()).default([]),
  categoryIds: z.array(z.number().int().positive()).default([]),
});
export type ListingDraftInput = z.infer<typeof listingDraftInput>;

export const searchQuery = z.object({
  cityKey: z.string().optional(),
  checkIn: isoDate.optional(),
  checkOut: isoDate.optional(),
  guests: z.coerce.number().int().min(1).optional(),
  minPrice: z.coerce.number().int().nonnegative().optional(),
  maxPrice: z.coerce.number().int().nonnegative().optional(),
  propertyType: z.enum(PROPERTY_TYPES).optional(),
  roomType: z.enum(ROOM_TYPES).optional(),
  amenityIds: z
    .string()
    .transform((s) => s.split(",").map(Number).filter((n) => Number.isInteger(n) && n > 0))
    .optional(),
  sort: z.enum(["relevance", "price_asc", "price_desc", "rating"]).default("relevance"),
  limit: z.coerce.number().int().min(1).max(50).default(24),
  cursor: z.string().optional(),
});
export type SearchQuery = z.infer<typeof searchQuery>;

export const amenityInput = z.object({
  key: z.string().regex(/^[a-z0-9_]+$/),
  category: z.enum(AMENITY_CATEGORIES),
  icon: z.string().min(1),
});

// ---------- availability ----------

export const hostBlockInput = z
  .object({ startDate: isoDate, endDate: isoDate, note: z.string().max(200).optional() })
  .refine((v) => v.endDate > v.startDate, { message: "endDate must be after startDate", path: ["endDate"] });

export const priceRuleInput = z
  .object({ startDate: isoDate, endDate: isoDate, nightlyPriceMinor: minor.min(1) })
  .refine((v) => v.endDate > v.startDate, { message: "endDate must be after startDate", path: ["endDate"] });

export const externalCalendarInput = z.object({
  name: z.string().min(1).max(60),
  url: z.url(),
});

// ---------- booking ----------

export const quoteInput = z.object({
  listingId: z.string().uuid(),
  checkIn: isoDate,
  checkOut: isoDate,
  guests: z.number().int().min(1),
});
export type QuoteInput = z.infer<typeof quoteInput>;

export const bookingRequestInput = quoteInput.extend({
  message: z.string().max(1000).optional(),
});
export type BookingRequestInput = z.infer<typeof bookingRequestInput>;

/** Who is asking, when they chose not to create an account. Name + phone is all staff need to run the stay. */
export const guestIdentityInput = z.object({
  name: z.string().trim().min(2).max(80),
  phone: z.string().regex(/^\d{8,15}$/, "digits only, with country code"),
  locale: z.enum(LOCALES).optional(),
});
export const guestBookingRequestInput = bookingRequestInput.extend({ guest: guestIdentityInput });
export type GuestBookingRequestInput = z.infer<typeof guestBookingRequestInput>;

/** Staff date block on a listing (owner stay, maintenance, a booking taken by phone elsewhere). */
export const staffBlockInput = z.object({ startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), note: z.string().max(200).optional() });
export const guestEditInput = z.object({ name: z.string().trim().min(2).max(80).optional(), phone: z.string().regex(/^\d{8,15}$/, "digits only, with country code").optional() });

export const reasonInput = z.object({ reason: z.string().min(3).max(500) });

// ---------- payments ----------

/** Proof of a transfer: the transaction reference, a screenshot of it, or both — never neither. */
export const submitPaymentInput = z
  .object({
    method: z.enum(PAYMENT_METHODS),
    reference: z.string().trim().max(120).optional(),
    proofKey: z.string().min(1).optional(),
  })
  .refine((v) => Boolean(v.reference) || Boolean(v.proofKey), { message: "Provide the transaction reference or a screenshot", path: ["reference"] });

export const rejectPaymentInput = z.object({ reason: z.string().min(3).max(300) });

// ---------- reviews ----------

const rating = z.number().int().min(1).max(5);
export const reviewInput = z.object({
  overall: rating,
  cleanliness: rating.optional(),
  accuracy: rating.optional(),
  communication: rating.optional(),
  location: rating.optional(),
  value: rating.optional(),
  comment: z.string().max(2000).optional(),
});
export type ReviewInput = z.infer<typeof reviewInput>;


// ---------- identity ----------

export const hostApplicationInput = z.object({
  displayName: z.string().min(2).max(80),
  bio: z.string().max(1000).optional(),
  languages: z.array(z.enum(LOCALES)).min(1),
});

export const idVerificationInput = z.object({
  docType: z.enum(["cin", "passport", "residence_card"]),
  frontKey: z.string().min(1),
  backKey: z.string().min(1).optional(),
});

/** Staff cancelling on someone's behalf must say whose decision it was — it changes who gets refunded what. */
export const staffCancelInput = z.discriminatedUnion("initiatedBy", [
  z.object({ initiatedBy: z.literal("guest"), reason: z.string().max(500).optional() }),
  z.object({ initiatedBy: z.literal("host"), reason: z.string().min(3).max(500) }),
]);
export type StaffCancelInput = z.infer<typeof staffCancelInput>;
